/**
 * HTTP Client - Unified API fetch wrapper
 * CRITICAL: Always attaches auth token + credentials for ALL requests
 */

import { readToken } from "../auth/token";

/**
 * Smart API base URL resolver.
 *
 * Problem: production builds (e.g. wassla.tech) were shipped with a
 * REACT_APP_BACKEND_URL that points at a different origin (the preview
 * deploy). Browsers then refuse the cross-origin fetch and we get the
 * dreaded "TypeError: Failed to fetch" (status 0). However, the live
 * domain ALSO proxies /api/* to the backend same-origin, so the safest
 * fix is: when the build-time URL is cross-origin AND we're on a custom
 * production domain, fall back to same-origin (empty base → relative URL).
 *
 * Preview / dev keep the build-time URL because that's how the ingress
 * is configured there.
 */
export function getApiBase() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (typeof window === 'undefined' || !window.location) return envUrl;
  try {
    const winOrigin = window.location.origin;
    // No build-time URL → already same-origin.
    if (!envUrl) return '';
    const envOrigin = new URL(envUrl).origin;
    // Same origin → no need for absolute URL, prefer relative.
    if (envOrigin === winOrigin) return '';
    // Cross-origin: keep env URL only on dev/preview/localhost hosts.
    const winHost = (window.location.hostname || '').toLowerCase();
    const isPreviewHost =
      winHost === 'localhost' ||
      winHost === '127.0.0.1' ||
      winHost.endsWith('.preview.emergentagent.com') ||
      winHost.endsWith('.emergentagent.com');
    if (!isPreviewHost) {
      // Production custom domain (e.g. wassla.tech) → same-origin proxy works,
      // build-time env URL is stale → use relative URLs.
      return '';
    }
    return envUrl;
  } catch (_e) {
    return envUrl;
  }
}

const API_URL = getApiBase();

/**
 * Parse response body ONCE - prevents "body stream already read" error
 */
async function parseResponseBody(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    try { return await res.json(); } catch { return null; }
  }
  try {
    const t = await res.text();
    return t ? { raw: t } : null;
  } catch {
    return null;
  }
}

/**
 * Unified API fetch wrapper
 * - Always attaches Authorization Bearer token (if exists)
 * - Always includes credentials for cookie support
 * - Returns request ID from headers for debugging
 * - Logs debug info in preview/dev environments
 */
export async function apiFetch(path, { method = "GET", body, headers = {}, timeoutMs = 60000 } = {}) {
  const token = readToken();
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const isForm = body instanceof FormData;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const finalHeaders = {
    Accept: "application/json",
    ...(isForm ? {} : body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  // DEV DEBUG - log auth status on preview environments
  const isPreview = typeof window !== "undefined" && window.location?.hostname?.includes("preview");
  if (isPreview) {
    console.log("[apiFetch]", method, path, { 
      hasToken: !!token, 
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });
  }

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
      credentials: "include", // IMPORTANT for cookie sessions
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    
    if (err.name === 'AbortError') {
      console.error('[apiFetch] Timeout:', path);
      return { ok: false, status: 0, data: { error: 'Request timed out' }, requestId: null };
    }
    
    console.error('[apiFetch] Network error:', path, err.message);
    return { ok: false, status: 0, data: { error: 'Network error', message: err.message }, requestId: null };
  } finally {
    clearTimeout(timer);
  }

  const data = await parseResponseBody(res);
  
  // Extract request ID from headers for debugging
  const requestId = res.headers.get("x-request-id") || null;

  // DEV DEBUG - log response status
  if (isPreview) {
    console.log("[apiFetch:res]", method, path, res.status, 
      data ? JSON.stringify(data).substring(0, 100) : 'null',
      requestId ? `(ref: ${requestId})` : '');
  }

  return { ok: res.ok, status: res.status, data, requestId };
}

/**
 * Convert API response to user-friendly error message
 * IMPORTANT: Only shows "log in again" for actual auth errors (401/403)
 * Returns object with message and optional missing fields
 */
export function toUserError(r) {
  if (r?.status === 401) {
    return "Session expired. Please log in again.";
  }
  if (r?.status === 403) {
    // Check for specific codes
    const code = r?.data?.detail?.code || r?.data?.code;
    if (code === 'ERR_BANNED_USER' || code === 'ACCOUNT_BANNED') {
      return "Your account has been banned.";
    }
    if (code === 'ERR_PENDING_BAN') {
      return "Your account is under review.";
    }
    if (code === 'ERR_SUSPENDED') {
      const hours = r?.data?.detail?.hours_remaining || r?.data?.hours_remaining;
      return hours ? `Account suspended. Try again in ${hours} hours.` : "Account temporarily suspended.";
    }
    return "Access denied. Please log in again.";
  }
  if (r?.status === 429) {
    const code = r?.data?.detail?.code || r?.data?.code;
    if (code === 'ERR_COOLDOWN') {
      const mins = r?.data?.detail?.minutes_remaining || r?.data?.minutes_remaining;
      return mins ? `Please wait ${Math.ceil(mins)} minutes before posting again.` : "Please wait before posting again.";
    }
    if (code === 'DAILY_LIMIT_REACHED') {
      return r?.data?.detail?.message || "Daily ad limit reached.";
    }
    return "Rate limited. Please try again later.";
  }
  if (r?.status === 400 || r?.status === 422) {
    // Validation error - try to get specific details
    const detail = r?.data?.detail;
    if (typeof detail === 'object' && detail !== null) {
      // Check for missing fields array
      if (detail.missing && Array.isArray(detail.missing)) {
        return `Missing required fields: ${detail.missing.join(', ')}`;
      }
      // Check for message
      if (detail.message) {
        return detail.message;
      }
    }
    if (typeof detail === 'string') {
      return detail;
    }
  }
  
  // For all other errors, show actual message with request ID if available
  const msg = r?.data?.detail?.message || 
         r?.data?.detail || 
         r?.data?.message || 
         r?.data?.error || 
         "Something went wrong. Please try again.";
  
  // Append request ID for debugging if available
  if (r?.requestId && typeof msg === 'string') {
    return `${msg} (ref: ${r.requestId})`;
  }
  
  return typeof msg === 'string' ? msg : "Something went wrong. Please try again.";
}

// Error codes
export const ErrorCodes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  PUBLISH_FAILED: 'PUBLISH_FAILED',
  RATE_LIMITED: 'RATE_LIMITED'
};

/**
 * Check if a response represents a network/connectivity error
 */
export function isNetworkError(r) {
  return r && !r.ok && r.status === 0;
}

export default { apiFetch, toUserError, ErrorCodes, isNetworkError };
